import React from "react";
import { observer } from "mobx-react";

import { emptyArrayToUndefined } from "@formant/core/common/emptyArrayToUndefined";
import { IFilter } from "@formant/core/model/IFilter";
import { IModule } from "@formant/core/model/IModule";
import { IObserveLayout } from "@formant/core/model/IObserveLayout";
import { IScopeFilter } from "@formant/core/model/IScopeFilter";
import { ITeleopLayout } from "@formant/core/model/ITeleopLayout";
import { ModuleType } from "@formant/core/model/ModuleType";
import { ControlModuleType } from "@formant/core/model/SpecialModuleTypes";

import { track } from "../../analytics";
import { useStores } from "../../context/StoreContext/useStores";
import { useViewContext } from "../../context/TeleopContext/ViewContext";
import {
    addModuleToBottomOfLayout,
    CustomModuleController,
    getDefaultSize,
} from "../CustomModuleController";
import { IItem } from "../ItemList/IItem";
import { HeaderButton } from "./HeaderButton";
import * as styles from "./ModuleAdd.scss";
import PickerTabGroup, { IPickerItem } from "./PickerTabGroup";
import { Popup } from "./Popup";
import { appTypeIcons, streamTypeIcons } from "./StreamIcon";

interface IModuleAdd {
    context: IScopeFilter;
    setItem: (item: string) => void;
    filter: IFilter;
}

interface IListApps {
    setItem: (item: IModule) => void;
    layout: ITeleopLayout | IObserveLayout;
}

export const removeStreamFromFilter = (
    name: string,
    filter: IFilter,
): IFilter => {
    const { names } = filter;
    if (names) {
        filter = {
            ...filter,
            names: emptyArrayToUndefined(names.filter(_ => _ !== name)),
        };
    }
    return filter;
};

export const toggleStreamFromFilter = (
    name: string,
    filter: IFilter,
): IFilter => {
    const { names } = filter;
    filter = {
        ...filter,
        names:
            names === undefined
                ? [name]
                : names.includes(name)
                  ? emptyArrayToUndefined(names.filter(_ => _ !== name))
                  : [...names, name],
    };
    if (filter.names === undefined) {
        filter.names = []; // if it is undefined the UI will show all modules
    }
    return filter;
};

export const ModuleAdd = observer(() => {
    const { authStore, customModuleStore, metadataStore, observeStore } =
        useStores();
    const listApps = ({ setItem, layout }: IListApps): IItem[] => {
        const customModules: IModule[] = customModuleStore.listAll() || [];
        const currentCustomStreamInfo = Object.entries(layout)
            .filter(([name]) => name.startsWith("custom-module:"))
            .map(([name, _]) => ({
                name,
                type: _.configuration?.moduleType || ("custom" as ModuleType),
            }));

        const existingNames = currentCustomStreamInfo.map(_ => _.name);
        const filtered = customModules.filter(
            module => !existingNames.includes(module.name),
        );

        return filtered.map(({ name }, i) => ({
            key: `${name}-${i}`,
            label: name,
            icon: appTypeIcons[name] || appTypeIcons.default,
            disabled: false,
            checked: false,
            toggled: false,
            onToggle: () => {
                setItem(filtered[i]);
            },
        }));
    };

    const listStreams = ({ context, filter, setItem }: IModuleAdd): IItem[] => {
        const allStreamsForDevice = metadataStore.listStreams(filter) || {};
        const enabledStreamsForDevice =
            metadataStore.listStreams(context) || {};

        const streamNames = Object.keys(allStreamsForDevice).map(s => ({
            stream: s,
            type: allStreamsForDevice[s],
        }));

        const streamEnabledNames = Object.keys(enabledStreamsForDevice).map(
            s => ({
                stream: s,
                type: enabledStreamsForDevice[s],
            }),
        );

        const streamsWeCanAdd = streamNames.filter(s => {
            return !streamEnabledNames.find(e => e.stream === s.stream);
        });

        return streamsWeCanAdd.map(({ stream, type }) => {
            return {
                key: stream,
                icon: streamTypeIcons[type],
                label: stream,
                disabled: false,
                checked: false,
                toggled: filter.names?.includes(stream),
                onToggle: (s: string) => setItem(s),
            };
        });
    };

    const [addingModule, setAddingModule] = React.useState<boolean>(false);
    const [selectedTab, setSelectedTab] = React.useState<string>("streams");
    const {
        device,
        view,
        context,
        layout,
        onSaveLayout,
        onChangeLayout,
        commitViewSave,
    } = useViewContext();

    const hasJoysticksAdded: boolean =
        Object.keys(layout).includes("joystick-controls");
    const hasButtonsAdded: boolean =
        Object.keys(layout).includes("button-controls");

    const customizeMode: boolean =
        observeStore.isCustomizing ||
        observeStore.configuringFlyout !== undefined;

    if (!customizeMode) {
        return null;
    }

    if (
        !authStore.hasAccess("teleop", "administrator") ||
        !view ||
        !context ||
        !layout
    ) {
        return null;
    }

    const setCustomize = (toggle: boolean) => {
        track("Navigator: Customize: Toggle", { toggle });
        observeStore.isCustomizing = toggle;
    };

    const saveControl = async (module: ControlModuleType) => {
        const { height, width } = getDefaultSize(module);
        const newLayout: ITeleopLayout = {
            [module]: addModuleToBottomOfLayout(
                layout as ITeleopLayout,
                width,
                height,
            ),
            ...(layout as ITeleopLayout),
        };
        view.layout = newLayout;
        await commitViewSave(view);
    };

    const saveApp = (module: IModule) => {
        setAddingModule(false);
        track("Navigator: Customize: Add App", { module: module.name });
        const ModuleController = CustomModuleController({
            layout,
            onChangeLayout: onSaveLayout,
        });
        const customModule = ModuleController.onAddDefinedCustomModule(module);
        track("Navigator: Customize: Inspecting", { module: customModule });
        observeStore.configuringFlyout = [
            layout,
            {
                streamName: customModule,
                moduleType: "custom",
                filter: view.filter || {},
                device,
            },
            onChangeLayout,
        ];
    };

    const saveStream = async (stream: string) => {
        if (!authStore.hasAccess("teleop", "administrator")) {
            // only admins can add modules
            return null;
        }

        setAddingModule(false);
        track("Navigator: Customize: Add Stream:", { module: stream });

        const newFilter = toggleStreamFromFilter(stream, view.filter || {});

        const newLayout: ITeleopLayout = {
            ...(layout as ITeleopLayout),
            [stream]: addModuleToBottomOfLayout(layout as ITeleopLayout, 4, 3),
        };

        const newView = {
            ...view,
            layout: newLayout,
            filter: newFilter,
        };

        await commitViewSave(newView);
    };

    const filter: IFilter = {
        deviceIds: context.deviceIds,
    };

    const items: IPickerItem[] = [
        // { // TODO: recent list
        //     id: "recent",
        //     name: "Recent",
        //     items: []
        // },
        {
            id: "streams",
            name: "Streams",
            items: listStreams({
                context,
                filter,
                setItem: saveStream,
            }),
        },
        {
            id: "apps",
            name: "Apps",
            items: listApps({
                setItem: saveApp,
                layout: (layout as ITeleopLayout) || {},
            }),
        },
    ];
    const controlItems = [];

    // if (!hasBothControlsAdded) {
    //     controlItems.push({
    //         key: `controls-0`,
    //         label: "Joystick And Button Controls",
    //         icon: appTypeIcons.controls || appTypeIcons.default,
    //         disabled: false,
    //         checked: false,
    //         toggled: false,
    //         onToggle: async () => {
    //             await saveControl("controls");
    //         }
    //     });
    // }
    if (!hasJoysticksAdded) {
        controlItems.push({
            key: `controls-1`,
            label: "Joystick Controls",
            icon: appTypeIcons.controls || appTypeIcons.default,
            disabled: false,
            checked: false,
            toggled: false,
            onToggle: async () => {
                track("Navigator: Customize: Add Joystick Controls");
                await saveControl("joystick-controls");
            },
        });
    }
    if (!hasButtonsAdded) {
        controlItems.push({
            key: `controls-2`,
            label: "Button Controls",
            icon: appTypeIcons.controls || appTypeIcons.default,
            disabled: false,
            checked: false,
            toggled: false,
            onToggle: async () => {
                track("Navigator: Customize: Add Button Controls");
                await saveControl("button-controls");
            },
        });
    }

    if (controlItems.length > 0) {
        items.push({
            id: "controls",
            name: "Controls",
            items: controlItems,
        });
    }
    // items.push({
    //     id: "hw-streams",
    //     name: "Hardware Streams",
    //     items: [
    //         {
    //             key: `hardware-streams-0`,
    //             label: "Controls",
    //             icon: streamTypeIcons.video,
    //             disabled: false,
    //             checked: false,
    //             toggled: false,
    //             onToggle: async () => {
    //                 // await saveControl(stream);
    //                 // await saveHwStream(stream);
    //             }
    //         }
    //     ]
    // });

    return (
        <>
            {!customizeMode && (
                <HeaderButton
                    icon="edit"
                    onClicked={() => {
                        track("Navigator: Customize");
                        setCustomize(true);
                    }}
                    data-tooltip="Customize"
                    data-testid="customize-button"
                />
            )}
            {customizeMode && (
                <HeaderButton
                    icon="plus"
                    data-tooltip="Add module"
                    data-testid="add-navigator-module"
                    onClicked={() => {
                        track("Navigator: Customize: Add Module");
                        setAddingModule(true);
                    }}
                />
            )}
            <Popup
                visible={addingModule}
                onClose={() => {
                    setAddingModule(false);
                }}
                title="Add module"
            >
                <div className={styles.picker}>
                    <PickerTabGroup
                        data-testid="add-module-picker"
                        selectedTab={selectedTab}
                        onClick={setSelectedTab}
                        items={items}
                    />
                </div>
            </Popup>
        </>
    );
});
